import { useTheme } from '@mui/material';
import { memo, useCallback } from 'react';
import { Address, Client, Conversation, Project } from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { AnySearchableEntity } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { ClientSidePanel } from 'src/pages/Clients/SidePanel/side-panel';
import { ConversationWindow } from 'src/pages/Conversations';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { ProjectSidePanel } from 'src/pages/Projects/SidePanel/side-panel';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { useOnEsc } from 'src/utils/hooks';

const GenericSidePanelComponent = ({
  entityName,
  selectedRecord,
  handleRecordClose,
}: {
  entityName: EntityNames;
  selectedRecord: AnySearchableEntity | null;
  handleRecordClose: () => void;
}) => {
  const theme = useTheme();
  const { updateConversation } = useConversationUpdate();

  const onUpdate = useCallback(
    async (conversation: Conversation) => {
      await updateConversation(conversation);
    },
    [updateConversation],
  );

  useOnEsc(handleRecordClose);

  if (!selectedRecord) {
    return;
  }

  if (entityName === EntityNames.Conversation) {
    return (
      <EntityWindowWrapper>
        <ConversationWindow
          onUpdate={onUpdate}
          conversation={selectedRecord as Conversation}
          onCloseConversation={handleRecordClose}
          isSplitView
        />
      </EntityWindowWrapper>
    );
  }

  if ([EntityNames.Client, EntityNames.Site, EntityNames.Project].includes(entityName)) {
    return (
      <EntityWindowWrapper
        sx={{
          [theme.breakpoints.up('sm')]: {
            minWidth: '400px',
          },
        }}
      >
        {entityName === EntityNames.Client && (
          <ClientSidePanel
            onClose={handleRecordClose}
            clientId={(selectedRecord as Client).clientId}
          />
        )}
        {entityName === EntityNames.Site && (
          <SiteSidePanel onClose={handleRecordClose} site={selectedRecord as Address} />
        )}
        {entityName === EntityNames.Project && (
          <ProjectSidePanel onClose={handleRecordClose} project={selectedRecord as Project} />
        )}
      </EntityWindowWrapper>
    );
  }

  return null;
};

export const GenericSidePanel = memo(GenericSidePanelComponent);
